<template>
  <div class="etiqueta-editor-page">
    <div v-if="loading" class="editor-loading">
      <div class="spinner spinner-primary spinner-lg"></div>
      <strong>Preparando o editor visual...</strong>
      <span>O modelo em uso não será alterado.</span>
    </div>

    <template v-else-if="model && definition">
      <header class="editor-header">
        <div class="editor-identity">
          <button
            type="button"
            class="btn btn-icon btn-light mr-3"
            title="Voltar aos modelos"
            @click="goBack"
          >
            <i class="fas fa-arrow-left"></i>
          </button>
          <span class="editor-symbol"><i class="fas fa-tags"></i></span>
          <div class="editor-title-wrap">
            <div class="d-flex align-items-center flex-wrap mb-1">
              <span :class="['editor-state', publicationStateClass]"
                ><i></i>{{ publicationStateLabel }}</span
              >
              <span class="editor-origin">{{
                model.origem === "legado"
                  ? `Origem: ${model.codigo_legado}`
                  : "Criada no editor"
              }}</span>
              <span class="editor-origin"
                ><i class="fas fa-building mr-1"></i
                >{{ scopeLabel(model) }}</span
              >
            </div>
            <input
              v-model.trim="name"
              class="editor-name"
              maxlength="120"
              aria-label="Nome do modelo"
              @input="markMetadataChanged"
            />
          </div>
        </div>

        <div class="editor-actions">
          <div class="history-actions">
            <button
              type="button"
              class="btn btn-icon btn-light"
              title="Desfazer"
              :disabled="!undoStack.length"
              @click="undo"
            >
              <i class="fas fa-undo"></i>
            </button>
            <button
              type="button"
              class="btn btn-icon btn-light"
              title="Refazer"
              :disabled="!redoStack.length"
              @click="redo"
            >
              <i class="fas fa-redo"></i>
            </button>
            <button
              type="button"
              class="btn btn-icon btn-light"
              title="Histórico de versões"
              @click="$bvModal.show('etiqueta-history-modal')"
            >
              <i class="fas fa-history"></i>
            </button>
          </div>
          <button
            type="button"
            class="btn btn-light-primary font-weight-bold"
            :class="{ 'spinner spinner-primary spinner-right': testing }"
            :disabled="testing || saving || publishing || discarding"
            @click="testPrint"
          >
            <i class="fas fa-print mr-2"></i>Impressão teste
            <span v-if="labelQuantity > 1">({{ labelQuantity }})</span>
          </button>
          <button
            type="button"
            class="btn btn-light font-weight-bold"
            :class="{ 'spinner spinner-dark spinner-right': saving }"
            :disabled="saving || publishing || discarding"
            @click="saveDraft()"
          >
            <i class="far fa-save mr-2"></i>Salvar rascunho
          </button>
          <button
            v-if="model.tem_rascunho"
            type="button"
            class="btn btn-light-danger font-weight-bold"
            :class="{ 'spinner spinner-danger spinner-right': discarding }"
            :disabled="saving || publishing || testing || discarding"
            @click="discardDraft"
          >
            <i class="fas fa-undo-alt mr-2"></i>{{ discardDraftLabel }}
          </button>
          <button
            type="button"
            class="btn btn-success font-weight-bold"
            :class="{ 'spinner spinner-light spinner-right': publishing }"
            :disabled="
              saving ||
              publishing ||
              testing ||
              discarding ||
              (!dirty && !model.tem_rascunho) ||
              !hasCurrentPrintApproval
            "
            :title="publishButtonTitle"
            @click="publish"
          >
            <i class="fas fa-check-circle mr-2"></i>Publicar
          </button>
        </div>
      </header>

      <div class="editor-layout">
        <aside class="editor-sidebar card card-custom">
          <b-tabs
            pills
            nav-class="sidebar-tabs"
            content-class="sidebar-content"
            fill
          >
            <b-tab active>
              <template #title
                ><i class="fas fa-plus-circle"></i
                ><span>Conteúdo</span></template
              >
              <field-palette
                :fields="bootstrap.campos || []"
                :print-options="effectiveTestOptions"
                :custom-text="customPrintText"
                :elements="definition.elementos || []"
                :selected-id="selectedId"
                :converted-legacy="Boolean(definition.importacao_legado)"
                @add="addElement"
                @select="selectedId = $event"
              />
            </b-tab>
            <b-tab>
              <template #title
                ><i class="fas fa-ruler-combined"></i
                ><span>Formato</span></template
              >
              <format-settings
                :definition="definition"
                @change-start="recordHistory"
                @update="replaceDefinition"
              />
            </b-tab>
            <b-tab>
              <template #title
                ><i class="fas fa-flask"></i><span>Exemplo</span></template
              >
              <div class="sample-panel">
                <div class="sample-heading">
                  <span class="etq-panel-kicker"
                    >Conferência e ajustes rápidos</span
                  >
                  <h5>Teste e tamanho dos textos</h5>
                  <p>
                    Troque os valores para conferir textos curtos, longos e
                    preços sem alterar produtos. O tamanho de fonte abaixo
                    altera a etiqueta de verdade.
                  </p>
                </div>
                <quick-font-settings
                  :elements="definition.elementos || []"
                  :print-options="effectiveTestOptions"
                  @change="setElementFontSize"
                />
                <div class="sample-company">
                  <div class="sample-company-label">
                    <span>Empresa usada no teste</span>
                    <small
                      >Define logo, razão social e dados exibidos na
                      etiqueta.</small
                    >
                  </div>
                  <div v-if="exampleCompany" class="selected-test-company">
                    <span><i class="fas fa-building"></i></span>
                    <div>
                      <strong>{{ companyName(exampleCompany) }}</strong>
                      <small v-if="model.escopo === 'empresa'"
                        >Empresa fixa deste modelo</small
                      >
                      <small v-else>Usada somente nesta prévia e teste</small>
                    </div>
                    <button
                      v-if="model.escopo === 'global'"
                      type="button"
                      class="btn btn-xs btn-light-primary font-weight-bold"
                      @click="changeExampleCompany"
                    >
                      Trocar
                    </button>
                  </div>
                  <template v-else-if="model.escopo === 'global'">
                    <div class="input-icon">
                      <input
                        v-model.trim="exampleCompanySearch"
                        class="form-control form-control-sm"
                        placeholder="Busque a empresa por nome ou CNPJ"
                        @input="scheduleExampleCompanySearch"
                      />
                      <span
                        ><i
                          :class="
                            searchingExampleCompanies
                              ? 'spinner spinner-sm spinner-primary'
                              : 'flaticon2-search-1 text-muted'
                          "
                        ></i
                      ></span>
                    </div>
                    <div
                      v-if="exampleCompanyResults.length"
                      class="test-company-results"
                    >
                      <button
                        v-for="company in exampleCompanyResults"
                        :key="company.id_empresa"
                        type="button"
                        @click="selectExampleCompany(company)"
                      >
                        <span>{{ companyName(company) }}</span>
                        <i class="fas fa-chevron-right"></i>
                      </button>
                    </div>
                    <small
                      v-else-if="
                        exampleCompanySearch && !searchingExampleCompanies
                      "
                      class="text-danger d-block mt-2"
                      >Nenhuma empresa encontrada.</small
                    >
                  </template>
                </div>
                <product-example-picker
                  v-if="exampleCompanyId"
                  :model-uuid="model.uuid"
                  :company-id="exampleCompanyId"
                  :selected-product="selectedExampleProduct"
                  :options="effectiveTestOptions"
                  @select="selectExampleProduct"
                  @clear="clearExampleProduct"
                />
                <legacy-print-options
                  :value="testOptions"
                  :custom-text="customPrintText"
                  @change="setLegacyPrintOption"
                  @update:custom-text="setCustomPrintText"
                />
                <div class="sample-fields">
                  <div
                    v-for="field in importantSampleFields"
                    :key="field.key"
                    class="form-group"
                  >
                    <label>{{ field.label }}</label>
                    <input
                      v-model="sampleValues[field.key]"
                      class="form-control form-control-sm"
                      :placeholder="field.sample"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-light-primary btn-block font-weight-bold"
                  @click="resetSampleValues"
                >
                  Restaurar dados de exemplo
                </button>
              </div>
            </b-tab>
          </b-tabs>
        </aside>

        <main class="editor-canvas-column">
          <div class="canvas-controls card card-custom">
            <div class="canvas-control-group">
              <span>Zoom</span>
              <button
                type="button"
                class="btn btn-xs btn-icon btn-light"
                :disabled="zoom <= 50"
                @click="zoom = Math.max(50, zoom - 10)"
              >
                <i class="fas fa-minus"></i>
              </button>
              <input
                v-model.number="zoom"
                type="range"
                min="50"
                max="200"
                step="10"
                aria-label="Zoom da etiqueta"
              />
              <button
                type="button"
                class="btn btn-xs btn-icon btn-light"
                :disabled="zoom >= 200"
                @click="zoom = Math.min(200, zoom + 10)"
              >
                <i class="fas fa-plus"></i>
              </button>
              <strong>{{ zoom }}%</strong>
            </div>
            <label class="grid-toggle">
              <span><i class="fas fa-border-all"></i>Grade</span>
              <b-form-checkbox v-model="showGrid" switch></b-form-checkbox>
            </label>
          </div>

          <label-canvas
            :definition="definition"
            :sample-values="sampleValues"
            :test-options="effectiveTestOptions"
            :example-company-id="exampleCompanyId"
            :selected-id="selectedId"
            :zoom="zoom"
            :show-grid="showGrid"
            @select="selectedId = $event"
            @change-start="recordHistory"
            @update="updateElement"
          />

          <print-engine-preview
            :pdf-url="enginePreviewPdfUrl"
            :loading="enginePreviewLoading"
            :error="enginePreviewError"
            :outdated="enginePreviewOutdated"
            :quantity="labelQuantity"
            :max-quantity="500"
            :warnings="warnings"
            :revision="enginePreviewRevision"
            @update:quantity="setLabelQuantity"
            @generate="requestEnginePreviewNow"
          />

          <div v-if="model.origem === 'legado'" class="conversion-note">
            <i class="fas fa-info-circle"></i>
            <div>
              <span v-if="needsLegacyReimport">
                <strong>Conversão criada pelo importador antigo:</strong> este
                rascunho pode ser recriado com a conversão atualizada. O legado
                continua em uso até uma publicação explícita.
              </span>
              <span v-else>
                <strong>Conversão inicial aproximada:</strong> confira medidas e
                faça uma impressão teste. O legado segue ativo enquanto esta
                versão não for publicada.
              </span>
              <button
                v-if="needsLegacyReimport"
                type="button"
                class="btn btn-xs btn-outline-warning font-weight-bold mt-2"
                :disabled="discarding"
                @click="reimportLegacyDraft"
              >
                <i class="fas fa-sync-alt mr-1"></i>Refazer conversão do legado
              </button>
            </div>
          </div>
        </main>

        <aside class="editor-right-column">
          <div class="card card-custom inspector-card">
            <element-inspector
              :element="selectedElement"
              :label="definition.etiqueta"
              :fields="bootstrap.campos || []"
              :fonts="bootstrap.fontes || []"
              :barcode-types="bootstrap.tipos_codigo_barras || []"
              :display-rules="bootstrap.regras_exibicao || []"
              @change-start="recordHistory"
              @update="updateElement"
              @duplicate="duplicateSelected"
              @delete="deleteSelected"
              @center-horizontal="centerSelectedHorizontally"
              @center-vertical="centerSelectedVertically"
              @front="bringSelectedToFront"
            />
          </div>
          <div class="card card-custom sheet-card">
            <sheet-preview
              :definition="definition"
              :warnings="warnings"
              compact
            />
          </div>
        </aside>
      </div>

      <b-modal
        id="etiqueta-history-modal"
        title="Histórico e recuperação"
        size="lg"
        centered
        hide-footer
      >
        <version-history
          :history="model.historico || []"
          :published-id="model.publicada ? model.publicada.id : null"
          :origin="model.origem"
          :has-publication="model.tem_publicacao"
          @restore="restoreVersion"
          @reactivate-legacy="reactivateLegacy"
        />
      </b-modal>
    </template>
  </div>
</template>

<script>
import etiquetasService from "@/modules/Etiquetas/services/etiquetasService";
import {
  clone,
  createElement,
  duplicateElement,
  layoutWarnings,
  placeElementWithoutOverlap,
  scopeLabel,
} from "@/modules/Etiquetas/services/etiquetaDefinition";
import {
  changeLegacyPrintOption,
  createLegacyPrintOptions,
  optionsWithCustomText,
} from "@/modules/Etiquetas/services/legacyPrintOptions";
import FieldPalette from "@/modules/Etiquetas/components/FieldPalette";
import FormatSettings from "@/modules/Etiquetas/components/FormatSettings";
import QuickFontSettings from "@/modules/Etiquetas/components/QuickFontSettings";
import LegacyPrintOptions from "@/modules/Etiquetas/components/LegacyPrintOptions";
import ProductExamplePicker from "@/modules/Etiquetas/components/ProductExamplePicker";
import LabelCanvas from "@/modules/Etiquetas/components/LabelCanvas";
import ElementInspector from "@/modules/Etiquetas/components/ElementInspector";
import PrintEnginePreview from "@/modules/Etiquetas/components/PrintEnginePreview";
import SheetPreview from "@/modules/Etiquetas/components/SheetPreview";
import VersionHistory from "@/modules/Etiquetas/components/VersionHistory";

export default {
  name: "EtiquetaEditor",
  components: {
    FieldPalette,
    FormatSettings,
    QuickFontSettings,
    LegacyPrintOptions,
    ProductExamplePicker,
    LabelCanvas,
    ElementInspector,
    PrintEnginePreview,
    SheetPreview,
    VersionHistory,
  },
  data() {
    return {
      loading: true,
      saving: false,
      testing: false,
      publishing: false,
      discarding: false,
      model: null,
      bootstrap: {},
      definition: null,
      name: "",
      description: "",
      selectedId: null,
      zoom: 100,
      showGrid: true,
      sampleValues: {},
      originalSampleValues: {},
      // Mesmos valores iniciais usados pela tela histórica Gerar etiquetas.
      testOptions: createLegacyPrintOptions(),
      savedSignature: "",
      undoStack: [],
      redoStack: [],
      allowNavigation: false,
      enginePreviewPdfUrl: "",
      enginePreviewLoading: false,
      enginePreviewError: "",
      enginePreviewRevision: 0,
      enginePreviewRequestId: 0,
      enginePreviewGeneratedSignature: "",
      labelQuantity: 1,
      exampleCompany: null,
      exampleCompanySearch: "",
      exampleCompanyResults: [],
      searchingExampleCompanies: false,
      exampleCompanyTimer: null,
      exampleCompanyRequestId: 0,
      selectedExampleProduct: null,
      selectedExampleProductId: null,
      printApprovalToken: "",
      printApprovalSignature: "",
      printApprovalExpiresAt: "",
      printTestRequestId: 0,
    };
  },
  computed: {
    selectedElement() {
      return (
        (this.definition.elementos || []).find(
          (element) => element.id === this.selectedId
        ) || null
      );
    },
    dirty() {
      if (!this.definition) return false;
      return this.signature() !== this.savedSignature;
    },
    warnings() {
      return layoutWarnings(this.definition);
    },
    publicationStateLabel() {
      if (this.model.tem_publicacao && this.model.tem_rascunho)
        return "Publicado + rascunho";
      if (this.model.tem_publicacao) return "Publicado";
      return "Somente rascunho";
    },
    publicationStateClass() {
      return this.model.tem_publicacao ? "published" : "draft";
    },
    discardDraftLabel() {
      return this.model &&
        this.model.origem === "legado" &&
        !this.model.tem_publicacao
        ? "Cancelar alterações e usar legado"
        : "Excluir rascunho";
    },
    customPrintText() {
      return String(this.sampleValues["impressao.texto_personalizado"] ?? "");
    },
    effectiveTestOptions() {
      return optionsWithCustomText(this.testOptions, this.customPrintText);
    },
    importantSampleFields() {
      const keys = [
        "produto.descricao",
        "produto.descricao_complementar",
        "produto.preco",
        "produto.preco_atacado",
        "produto.peso",
        "produto.codigo_barras",
        "produto.codigo",
        "produto.referencia",
        "produto.lote",
        "produto.validade",
        "empresa.nome_fantasia",
      ];
      return (this.bootstrap.campos || []).filter((field) =>
        keys.includes(field.key)
      );
    },
    enginePreviewInputSignature() {
      if (!this.definition || !this.model) return "";
      return JSON.stringify({
        modelo_uuid: this.model.uuid,
        definicao: this.definition,
        dados_exemplo: this.sampleValues,
        opcoes: this.effectiveTestOptions,
        quantidade_etiquetas: this.labelQuantity,
        id_empresa_exemplo: this.exampleCompanyId,
        id_produto_exemplo: this.selectedExampleProductId,
      });
    },
    exampleCompanyId() {
      if (!this.model) return null;
      if (this.model.escopo === "empresa") return this.model.id_empresa;
      return this.exampleCompany ? this.exampleCompany.id_empresa : null;
    },
    enginePreviewOutdated() {
      return Boolean(
        this.enginePreviewPdfUrl &&
          this.enginePreviewGeneratedSignature !==
            this.enginePreviewInputSignature
      );
    },
    currentPrintApprovalSignature() {
      return this.enginePreviewInputSignature;
    },
    hasCurrentPrintApproval() {
      return Boolean(
        this.printApprovalToken &&
          this.printApprovalSignature === this.currentPrintApprovalSignature &&
          (!this.printApprovalExpiresAt ||
            Date.parse(this.printApprovalExpiresAt) >= Date.now())
      );
    },
    publishButtonTitle() {
      return this.hasCurrentPrintApproval
        ? "Publicar exatamente a versão aprovada na impressão teste"
        : "Faça uma impressão teste desta versão antes de publicar";
    },
    needsLegacyReimport() {
      return Boolean(
        this.model &&
          this.model.origem === "legado" &&
          !this.model.tem_publicacao &&
          this.definition &&
          !this.definition.importacao_legado
      );
    },
  },
  async mounted() {
    window.addEventListener("beforeunload", this.beforeUnload);
    window.addEventListener("keydown", this.handleKeyboard);
    await this.load();
  },
  beforeDestroy() {
    this.cancelEnginePreview();
    this.printTestRequestId += 1;
    if (this.exampleCompanyTimer) clearTimeout(this.exampleCompanyTimer);
    this.exampleCompanyRequestId += 1;
    window.removeEventListener("beforeunload", this.beforeUnload);
    window.removeEventListener("keydown", this.handleKeyboard);
  },
  beforeRouteLeave(to, from, next) {
    if (this.allowNavigation || !this.dirty) {
      next();
      return;
    }
    this.$bvModal
      .msgBoxConfirm(
        "Existem alterações não salvas. Deseja sair e descartá-las?",
        {
          title: "Descartar alterações?",
          okTitle: "Sair sem salvar",
          cancelTitle: "Continuar editando",
          okVariant: "danger",
          centered: true,
        }
      )
      .then((confirmed) => next(Boolean(confirmed)))
      .catch(() => next(false));
  },
  methods: {
    scopeLabel,
    setLegacyPrintOption({ key, enabled }) {
      this.testOptions = changeLegacyPrintOption(
        this.testOptions,
        key,
        enabled
      );
    },
    setCustomPrintText(value) {
      this.$set(
        this.sampleValues,
        "impressao.texto_personalizado",
        String(value ?? "").slice(0, 150)
      );
    },
    async load() {
      this.loading = true;
      try {
        const [bootstrap, model] = await Promise.all([
          etiquetasService.inicializacao(),
          etiquetasService.find(this.$route.params.uuid),
        ]);
        this.bootstrap = bootstrap.data || {};
        this.originalSampleValues = clone(this.bootstrap.dados_exemplo || {});
        this.sampleValues = clone(this.originalSampleValues);
        this.applyModel(model.data, true);
        await this.initializeExampleCompany();
      } catch (error) {
        this.$router.replace({ name: "etiquetasLista" }).catch(() => {});
      } finally {
        this.loading = false;
      }
    },
    applyModel(model, resetHistory = false) {
      const previousDefinitionSignature = this.definition
        ? this.currentPrintApprovalSignature
        : "";
      this.model = model;
      this.name = model.nome || "Modelo sem nome";
      this.description = model.descricao || "";
      const version = model.rascunho || model.publicada;
      if (!version || !version.definicao)
        throw new Error("Modelo sem definição");
      this.definition = clone(version.definicao);
      if (
        previousDefinitionSignature &&
        previousDefinitionSignature !== this.currentPrintApprovalSignature
      ) {
        this.invalidatePrintApproval();
      }
      this.savedSignature = this.signature();
      if (resetHistory) {
        this.undoStack = [];
        this.redoStack = [];
      }
      if (
        this.selectedId &&
        !(this.definition.elementos || []).some(
          (item) => item.id === this.selectedId
        )
      ) {
        this.selectedId = null;
      }
    },
    requestEnginePreviewNow() {
      return this.loadEnginePreview();
    },
    async loadEnginePreview() {
      if (!this.definition || !this.model) return;
      if (!this.ensureExampleCompany()) return;

      const requestId = ++this.enginePreviewRequestId;
      const generatedSignature = this.enginePreviewInputSignature;
      this.enginePreviewLoading = true;
      this.enginePreviewError = "";

      try {
        const { data } = await etiquetasService.previewPdf({
          modelo_uuid: this.model.uuid,
          definicao: clone(this.definition),
          dados_exemplo: clone(this.sampleValues),
          opcoes: clone(this.effectiveTestOptions),
          quantidade_etiquetas: this.labelQuantity,
          id_empresa_exemplo: this.exampleCompanyId,
          id_produto_exemplo: this.selectedExampleProductId,
        });
        if (requestId !== this.enginePreviewRequestId) return;

        const pdfBlob =
          data instanceof Blob
            ? data
            : new Blob([data], { type: "application/pdf" });
        if (!pdfBlob.size) throw new Error("O servidor retornou um PDF vazio.");

        const nextUrl = window.URL.createObjectURL(pdfBlob);
        this.revokeEnginePreviewUrl();
        this.enginePreviewPdfUrl = nextUrl;
        this.enginePreviewRevision += 1;
        this.enginePreviewGeneratedSignature = generatedSignature;
      } catch (error) {
        if (requestId !== this.enginePreviewRequestId) return;
        const message = await this.enginePreviewErrorMessage(error);
        if (requestId !== this.enginePreviewRequestId) return;
        this.enginePreviewError = message;
      } finally {
        if (requestId === this.enginePreviewRequestId) {
          this.enginePreviewLoading = false;
        }
      }
    },
    cancelEnginePreview() {
      this.enginePreviewRequestId += 1;
      this.enginePreviewLoading = false;
      this.revokeEnginePreviewUrl();
    },
    setLabelQuantity(value) {
      const parsed = Number.parseInt(value, 10);
      this.labelQuantity = Math.min(
        500,
        Math.max(1, Number.isNaN(parsed) ? 1 : parsed)
      );
    },
    companyName(company) {
      return (
        company.nome_fantasia || company.nome || `Empresa ${company.id_empresa}`
      );
    },
    async initializeExampleCompany() {
      if (this.model.escopo === "empresa") {
        this.exampleCompany = this.model.empresa || {
          id_empresa: this.model.id_empresa,
          nome_fantasia: `Empresa ${this.model.id_empresa}`,
        };
        return;
      }

      this.searchingExampleCompanies = true;
      try {
        const { data } = await etiquetasService.buscarEmpresas("Nuts");
        const companies = Array.isArray(data) ? data : [];
        this.exampleCompany =
          companies.find((company) =>
            this.companyName(company).toLowerCase().includes("nuts")
          ) ||
          companies[0] ||
          null;
      } catch (error) {
        this.exampleCompany = null;
      } finally {
        this.searchingExampleCompanies = false;
      }
    },
    changeExampleCompany() {
      this.exampleCompanyRequestId += 1;
      this.clearExampleProduct();
      this.exampleCompany = null;
      this.exampleCompanySearch = "";
      this.exampleCompanyResults = [];
      this.$nextTick(() => this.searchExampleCompanies());
    },
    scheduleExampleCompanySearch() {
      if (this.exampleCompanyTimer) clearTimeout(this.exampleCompanyTimer);
      this.exampleCompanyRequestId += 1;
      this.exampleCompanyTimer = setTimeout(() => {
        this.exampleCompanyTimer = null;
        this.searchExampleCompanies();
      }, 280);
    },
    async searchExampleCompanies() {
      const term = this.exampleCompanySearch;
      const requestId = this.exampleCompanyRequestId;
      this.searchingExampleCompanies = true;
      try {
        const { data } = await etiquetasService.buscarEmpresas(term);
        if (
          requestId !== this.exampleCompanyRequestId ||
          term !== this.exampleCompanySearch
        )
          return;
        this.exampleCompanyResults = Array.isArray(data) ? data : [];
      } catch (error) {
        if (requestId !== this.exampleCompanyRequestId) return;
        this.exampleCompanyResults = [];
      } finally {
        if (requestId === this.exampleCompanyRequestId)
          this.searchingExampleCompanies = false;
      }
    },
    selectExampleCompany(company) {
      this.exampleCompanyRequestId += 1;
      this.clearExampleProduct();
      this.exampleCompany = company;
      this.exampleCompanySearch = "";
      this.exampleCompanyResults = [];
    },
    ensureExampleCompany() {
      if (this.exampleCompanyId) return true;
      this.$toast.warning("Selecione a empresa usada no teste.");
      return false;
    },
    selectExampleProduct({ product, values }) {
      this.selectedExampleProduct = product;
      this.selectedExampleProductId = Number(product.id);
      Object.entries(values || {}).forEach(([key, value]) => {
        this.$set(this.sampleValues, key, value);
      });
    },
    clearExampleProduct() {
      this.selectedExampleProduct = null;
      this.selectedExampleProductId = null;
      const defaults = this.originalSampleValues || {};
      Object.keys(this.sampleValues || {})
        .filter((key) => key.startsWith("produto."))
        .forEach((key) => {
          if (Object.prototype.hasOwnProperty.call(defaults, key)) {
            this.$set(this.sampleValues, key, clone(defaults[key]));
          } else {
            this.$delete(this.sampleValues, key);
          }
        });
    },
    revokeEnginePreviewUrl() {
      if (!this.enginePreviewPdfUrl) return;
      window.URL.revokeObjectURL(this.enginePreviewPdfUrl);
      this.enginePreviewPdfUrl = "";
    },
    async enginePreviewErrorMessage(error) {
      const responseData = error?.response?.data;
      if (
        responseData instanceof Blob &&
        typeof responseData.text === "function"
      ) {
        try {
          const body = JSON.parse(await responseData.text());
          if (body && body.message) return body.message;
        } catch (parseError) {
          // A resposta binária não continha uma mensagem JSON utilizável.
        }
      }

      return (
        responseData?.message ||
        error?.message ||
        "A prévia oficial está temporariamente indisponível. Tente novamente."
      );
    },
    signature() {
      return JSON.stringify({
        name: this.name,
        description: this.description,
        definition: this.definition,
      });
    },
    invalidatePrintApproval() {
      this.printApprovalToken = "";
      this.printApprovalSignature = "";
      this.printApprovalExpiresAt = "";
    },
    markMetadataChanged() {
      // A assinatura computada já identifica a alteração; o evento mantém a intenção explícita.
    },
    recordHistory() {
      if (!this.definition) return;
      const snapshot = JSON.stringify(this.definition);
      if (this.undoStack[this.undoStack.length - 1] !== snapshot) {
        this.undoStack.push(snapshot);
        if (this.undoStack.length > 50) this.undoStack.shift();
      }
      this.redoStack = [];
    },
    undo() {
      if (!this.undoStack.length) return;
      this.redoStack.push(JSON.stringify(this.definition));
      this.definition = JSON.parse(this.undoStack.pop());
      this.ensureSelection();
    },
    redo() {
      if (!this.redoStack.length) return;
      this.undoStack.push(JSON.stringify(this.definition));
      this.definition = JSON.parse(this.redoStack.pop());
      this.ensureSelection();
    },
    ensureSelection() {
      if (
        this.selectedId &&
        !(this.definition.elementos || []).some(
          (item) => item.id === this.selectedId
        )
      )
        this.selectedId = null;
    },
    addElement(type, field = null) {
      let element = createElement({
        type,
        field,
        label: this.definition.etiqueta,
      });
      element = placeElementWithoutOverlap(
        element,
        this.definition.elementos || [],
        this.definition.etiqueta
      );
      if (!element) {
        this.$toast.warning(
          "Não há espaço livre para adicionar este conteúdo. Mova ou reduza os elementos existentes e tente novamente."
        );
        return;
      }
      this.recordHistory();
      const maxOrder = Math.max(
        0,
        ...(this.definition.elementos || []).map((item) =>
          Number(item.ordem || 0)
        )
      );
      element.ordem = maxOrder + 1;
      this.definition.elementos.push(element);
      this.selectedId = element.id;
      this.$nextTick(() => {
        const right = document.querySelector(".editor-right-column");
        if (right && window.innerWidth < 1200)
          right.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    updateElement(next) {
      const index = this.definition.elementos.findIndex(
        (element) => element.id === next.id
      );
      if (index !== -1) this.$set(this.definition.elementos, index, next);
    },
    setElementFontSize({ id, size }) {
      const element = (this.definition.elementos || []).find(
        (item) => item.id === id
      );
      const parsed = Number(String(size).replace(",", "."));
      if (!element || !Number.isFinite(parsed)) return;

      const normalized = Math.round(Math.min(72, Math.max(4, parsed)) * 2) / 2;
      if (Number(element.estilo?.tamanho_pt || 8) === normalized) return;

      this.recordHistory();
      const next = clone(element);
      next.estilo = {
        ...(next.estilo || {}),
        tamanho_pt: normalized,
      };
      this.updateElement(next);
    },
    replaceDefinition(next) {
      this.definition = next;
      this.ensureSelection();
    },
    duplicateSelected() {
      if (!this.selectedElement) return;
      this.recordHistory();
      const copy = duplicateElement(
        this.selectedElement,
        this.definition.etiqueta
      );
      this.definition.elementos.push(copy);
      this.selectedId = copy.id;
    },
    centerSelectedHorizontally() {
      if (!this.selectedElement) return;
      this.recordHistory();
      const next = clone(this.selectedElement);
      const label = this.definition.etiqueta || {};
      next.x_mm = Number(
        Math.max(
          0,
          (Number(label.largura_mm || 0) - Number(next.largura_mm || 0)) / 2
        ).toFixed(2)
      );
      this.updateElement(next);
    },
    centerSelectedVertically() {
      if (!this.selectedElement) return;
      this.recordHistory();
      const next = clone(this.selectedElement);
      const label = this.definition.etiqueta || {};
      next.y_mm = Number(
        Math.max(
          0,
          (Number(label.altura_mm || 0) - Number(next.altura_mm || 0)) / 2
        ).toFixed(2)
      );
      this.updateElement(next);
    },
    deleteSelected() {
      if (!this.selectedElement) return;
      this.recordHistory();
      this.definition.elementos = this.definition.elementos.filter(
        (item) => item.id !== this.selectedId
      );
      this.selectedId = null;
    },
    bringSelectedToFront() {
      if (!this.selectedElement) return;
      this.recordHistory();
      const next = clone(this.selectedElement);
      next.ordem =
        Math.max(
          0,
          ...this.definition.elementos.map((item) => Number(item.ordem || 0))
        ) + 1;
      this.updateElement(next);
    },
    handleKeyboard(event) {
      const tag = String(event.target?.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag)) return;
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z") {
        event.preventDefault();
        if (event.shiftKey) this.redo();
        else this.undo();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "y") {
        event.preventDefault();
        this.redo();
        return;
      }
      if (["Delete", "Backspace"].includes(event.key) && this.selectedElement) {
        event.preventDefault();
        this.deleteSelected();
        return;
      }
      if (
        ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(
          event.key
        ) &&
        this.selectedElement
      ) {
        event.preventDefault();
        this.nudgeSelected(event.key, event.shiftKey ? 1 : 0.1);
      }
    },
    nudgeSelected(key, amount) {
      this.recordHistory();
      const next = clone(this.selectedElement);
      const maxX = Math.max(
        0,
        Number(this.definition.etiqueta.largura_mm) - Number(next.largura_mm)
      );
      const maxY = Math.max(
        0,
        Number(this.definition.etiqueta.altura_mm) - Number(next.altura_mm)
      );
      if (key === "ArrowLeft")
        next.x_mm = Math.max(0, Number(next.x_mm) - amount);
      if (key === "ArrowRight")
        next.x_mm = Math.min(maxX, Number(next.x_mm) + amount);
      if (key === "ArrowUp")
        next.y_mm = Math.max(0, Number(next.y_mm) - amount);
      if (key === "ArrowDown")
        next.y_mm = Math.min(maxY, Number(next.y_mm) + amount);
      next.x_mm = Number(next.x_mm.toFixed(2));
      next.y_mm = Number(next.y_mm.toFixed(2));
      this.updateElement(next);
    },
    async saveDraft(silent = false) {
      if (!this.name) {
        this.$toast.warning("Informe um nome para o modelo.");
        return false;
      }
      this.saving = true;
      try {
        const base = this.model.rascunho || this.model.publicada;
        const { data } = await etiquetasService.salvarRascunho({
          modelo_uuid: this.model.uuid,
          nome: this.name,
          descricao: this.description || null,
          definicao: this.definition,
          checksum_base: base ? base.checksum : null,
          token_revisao: this.model.token_revisao,
        });
        this.applyModel(data);
        if (!silent)
          this.$toast.success(
            "Rascunho salvo. A impressão em uso não foi alterada."
          );
        return true;
      } catch (error) {
        return false;
      } finally {
        this.saving = false;
      }
    },
    async discardDraft() {
      if (!this.model || !this.model.tem_rascunho) return;
      const returnsToLegacy =
        this.model.origem === "legado" && !this.model.tem_publicacao;
      const removesModel = !returnsToLegacy && !this.model.tem_publicacao;
      const result = await this.$swal.fire({
        icon: "warning",
        title: returnsToLegacy
          ? "Cancelar alterações e usar o legado?"
          : "Excluir este rascunho?",
        text: returnsToLegacy
          ? "A versão feita no editor será removida e o motor legado voltará a ser usado neste escopo."
          : removesModel
          ? "Este modelo ainda não foi publicado e será removido da lista."
          : "As alterações em andamento serão removidas. A versão publicada continuará em uso.",
        showCancelButton: true,
        confirmButtonText: this.discardDraftLabel,
        confirmButtonColor: "#f64e60",
        cancelButtonText: "Voltar",
      });
      if (!result.isConfirmed) return;

      this.discarding = true;
      try {
        const { data } = await etiquetasService.descartarRascunho(
          this.model.uuid,
          this.model.token_revisao
        );
        if (data.arquivado || (!data.rascunho && !data.publicada)) {
          this.allowNavigation = true;
          this.$toast.success(
            returnsToLegacy
              ? "Alterações canceladas. A etiqueta legada está em uso."
              : "Rascunho excluído."
          );
          await this.$router.replace({ name: "etiquetasLista" });
          return;
        }

        this.applyModel(data, true);
        this.$toast.success(
          "Rascunho excluído. A versão publicada continua em uso."
        );
      } catch (error) {
        // O interceptor apresenta a mensagem da API.
      } finally {
        this.discarding = false;
      }
    },
    async testPrint() {
      if (!this.ensureExampleCompany()) return;

      const previewWindow = window.open("", "_blank");
      if (!previewWindow) {
        this.$toast.warning(
          "Permita a abertura da janela do PDF para concluir a impressão teste."
        );
        return;
      }

      try {
        previewWindow.document.write(
          "<title>Gerando impressão teste...</title><p style='font-family:Arial;padding:30px'>Gerando impressão teste...</p>"
        );
      } catch (error) {
        if (!previewWindow.closed) previewWindow.close();
        this.$toast.warning(
          "Não foi possível preparar a janela da impressão teste. Tente novamente."
        );
        return;
      }

      const requestId = ++this.printTestRequestId;
      this.testing = true;
      try {
        const definitionBeforeSave = clone(this.definition);
        if (this.dirty || !this.model.rascunho) {
          const saved = await this.saveDraft(true);
          if (!saved) {
            if (!previewWindow.closed) previewWindow.close();
            return;
          }
        }

        if (previewWindow.closed) {
          this.$toast.warning(
            "A janela do PDF foi fechada. Gere uma nova impressão teste para aprovar."
          );
          return;
        }

        const savedChecksum = String(this.model?.rascunho?.checksum || "");
        if (!savedChecksum) {
          previewWindow.close();
          this.$toast.warning(
            "Não foi possível confirmar a versão salva. Salve o rascunho e tente novamente."
          );
          return;
        }

        if (
          JSON.stringify(definitionBeforeSave) !==
          JSON.stringify(this.definition)
        ) {
          previewWindow.close();
          this.$toast.warning(
            "O servidor ajustou o rascunho ao salvar. Confira o desenho atualizado e gere a impressão teste novamente."
          );
          return;
        }

        const approvalRequestSignature = this.currentPrintApprovalSignature;
        const requestPayload = {
          modelo_uuid: this.model.uuid,
          definicao: clone(this.definition),
          dados_exemplo: clone(this.sampleValues),
          opcoes: clone(this.effectiveTestOptions),
          quantidade_etiquetas: this.labelQuantity,
          id_empresa_exemplo: this.exampleCompanyId,
          id_produto_exemplo: this.selectedExampleProductId,
        };
        const { data } = await etiquetasService.impressaoTeste({
          ...requestPayload,
        });

        if (requestId !== this.printTestRequestId) {
          if (!previewWindow.closed) previewWindow.close();
          return;
        }

        if (approvalRequestSignature !== this.currentPrintApprovalSignature) {
          if (!previewWindow.closed) previewWindow.close();
          this.$toast.warning(
            "A etiqueta ou os dados de teste mudaram durante a geração. Gere uma nova impressão teste."
          );
          return;
        }

        if (!data.token_impressao_teste) {
          throw new Error(
            "O servidor não confirmou a aprovação desta impressão teste."
          );
        }

        if (
          !data.checksum_testado ||
          String(data.checksum_testado) !== savedChecksum
        ) {
          if (!previewWindow.closed) previewWindow.close();
          this.$toast.warning(
            "O PDF de teste não corresponde ao rascunho salvo. Salve e gere uma nova impressão teste."
          );
          return;
        }

        if (!data.url_etiqueta || previewWindow.closed) {
          if (!previewWindow.closed) previewWindow.close();
          this.$toast.warning(
            "O PDF de teste não pôde ser apresentado. Gere uma nova impressão antes de publicar."
          );
          return;
        }

        try {
          previewWindow.location = data.url_etiqueta;
        } catch (error) {
          if (!previewWindow.closed) previewWindow.close();
          this.$toast.warning(
            "O PDF de teste não pôde ser apresentado. Gere uma nova impressão antes de publicar."
          );
          return;
        }

        if (previewWindow.closed) {
          this.$toast.warning(
            "A janela do PDF foi fechada. Gere uma nova impressão teste para aprovar."
          );
          return;
        }

        this.printApprovalToken = data.token_impressao_teste;
        this.printApprovalSignature = approvalRequestSignature;
        this.printApprovalExpiresAt = data.teste_expira_em || "";
        this.$toast.success("PDF de teste gerado. Nada foi publicado.");
      } catch (error) {
        if (!previewWindow.closed) previewWindow.close();
      } finally {
        this.testing = false;
      }
    },
    async publish() {
      if (!this.hasCurrentPrintApproval) {
        this.$toast.warning(
          "Faça uma impressão teste desta versão antes de publicar."
        );
        return;
      }
      if (this.dirty || !this.model.rascunho) {
        const saved = await this.saveDraft(true);
        if (!saved) return;
      }
      if (!this.hasCurrentPrintApproval) {
        this.$toast.warning(
          "A etiqueta mudou depois do teste. Gere uma nova impressão teste."
        );
        return;
      }
      const firstLegacyPublication =
        this.model.origem === "legado" && !this.model.tem_publicacao;
      const result = await this.$swal.fire({
        icon: firstLegacyPublication ? "warning" : "question",
        title: firstLegacyPublication
          ? "Substituir o legado neste escopo?"
          : "Publicar esta versão?",
        html: firstLegacyPublication
          ? `<p>A etiqueta <strong>${this.escapeHtml(
              this.model.codigo_legado
            )}</strong> passará a usar este desenho para <strong>${this.escapeHtml(
              scopeLabel(this.model)
            )}</strong>.</p><p class="text-muted font-size-sm">As demais empresas e modelos legados não serão alterados. Você poderá reativar o legado pelo histórico.</p>`
          : `<p>Esta versão passará a ser usada nas próximas impressões para <strong>${this.escapeHtml(
              scopeLabel(this.model)
            )}</strong>.</p><p class="text-muted font-size-sm">A versão anterior continuará guardada no histórico.</p>`,
        showCancelButton: true,
        confirmButtonText: firstLegacyPublication
          ? "Sim, publicar e substituir"
          : "Sim, publicar",
        cancelButtonText: "Voltar e conferir",
        confirmButtonColor: "#1bc5bd",
      });
      if (!result.isConfirmed) return;

      this.publishing = true;
      try {
        const { data } = await etiquetasService.publicar({
          modelo_uuid: this.model.uuid,
          checksum: this.model.rascunho.checksum,
          token_revisao: this.model.token_revisao,
          token_impressao_teste: this.printApprovalToken,
          observacao: "Publicação pelo editor visual",
        });
        this.applyModel(data, true);
        this.$toast.success("Versão publicada com sucesso.");
      } catch (error) {
        // O interceptor apresenta a mensagem da API.
      } finally {
        this.publishing = false;
      }
    },
    async restoreVersion(version) {
      const result = await this.$swal.fire({
        icon: "warning",
        title: `Usar a versão ${version.numero} como novo rascunho?`,
        text: "A etiqueta atualmente em uso não será alterada. Revise e faça uma impressão teste antes de publicar.",
        showCancelButton: true,
        confirmButtonText: "Criar rascunho",
        cancelButtonText: "Cancelar",
      });
      if (!result.isConfirmed) return;
      try {
        const { data } = await etiquetasService.restaurar({
          modelo_uuid: this.model.uuid,
          versao_id: version.id,
          token_revisao: this.model.token_revisao,
          observacao: `Restauração solicitada pelo editor da versão ${version.numero}`,
        });
        this.applyModel(data, true);
        this.$bvModal.hide("etiqueta-history-modal");
        this.$toast.success(
          `Versão ${version.numero} restaurada como rascunho. Gere a impressão teste antes de publicar.`
        );
      } catch (error) {
        // O interceptor apresenta a mensagem da API.
      }
    },
    async reimportLegacyDraft() {
      if (!this.needsLegacyReimport) return;
      const result = await this.$swal.fire({
        icon: "warning",
        title: "Refazer a conversão do legado?",
        html: "<p>O rascunho atual será descartado e recriado pelo importador atualizado.</p><p class='text-muted font-size-sm'>A etiqueta legada em uso não será alterada.</p>",
        showCancelButton: true,
        confirmButtonText: "Sim, refazer conversão",
        confirmButtonColor: "#ffa800",
        cancelButtonText: "Manter rascunho atual",
      });
      if (!result.isConfirmed) return;

      this.discarding = true;
      try {
        await etiquetasService.descartarRascunho(
          this.model.uuid,
          this.model.token_revisao
        );
        const { data } = await etiquetasService.iniciarLegado({
          codigo_legado: this.model.codigo_legado,
          escopo: this.model.escopo,
          id_empresa_destino:
            this.model.escopo === "empresa" ? this.model.id_empresa : null,
        });
        this.invalidatePrintApproval();
        this.applyModel(data, true);
        this.$toast.success(
          "Conversão refeita. Confira o desenho e gere uma nova impressão teste."
        );
      } catch (error) {
        // O interceptor apresenta a mensagem da API.
      } finally {
        this.discarding = false;
      }
    },
    async reactivateLegacy() {
      const result = await this.$swal.fire({
        icon: "warning",
        title: "Reativar a etiqueta legada?",
        html: "<p>O motor original voltará a ser usado imediatamente neste escopo.</p><p class='text-muted font-size-sm'>As versões do editor serão preservadas no histórico.</p>",
        showCancelButton: true,
        confirmButtonText: "Sim, reativar legado",
        confirmButtonColor: "#f64e60",
        cancelButtonText: "Cancelar",
      });
      if (!result.isConfirmed) return;
      try {
        const { data } = await etiquetasService.reativarLegado(
          this.model.uuid,
          this.model.token_revisao
        );
        this.applyModel(data, true);
        this.$bvModal.hide("etiqueta-history-modal");
        this.$toast.success("Motor legado reativado para este modelo.");
      } catch (error) {
        // O interceptor apresenta a mensagem da API.
      }
    },
    resetSampleValues() {
      this.selectedExampleProduct = null;
      this.selectedExampleProductId = null;
      this.sampleValues = clone(this.originalSampleValues);
    },
    goBack() {
      this.$router.push({ name: "etiquetasLista" });
    },
    beforeUnload(event) {
      if (!this.dirty) return undefined;
      event.preventDefault();
      event.returnValue = "";
      return "";
    },
    escapeHtml(value) {
      const div = document.createElement("div");
      div.textContent = String(value || "");
      return div.innerHTML;
    },
  },
};
</script>

<style lang="scss" scoped>
.etiqueta-editor-page {
  --etq-global-header-height: 45px;
  --etq-editor-header-height: 78px;
  margin: -12px -12px 0;
  padding-bottom: 35px;
}

.editor-loading {
  min-height: 520px;
  border-radius: 12px;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;

  strong {
    margin-top: 12px;
    color: #3f4254;
  }
  span {
    color: #7e8299;
    font-size: 11px;
  }
}

.editor-header {
  position: sticky;
  top: var(--etq-global-header-height);
  z-index: 90;
  min-height: 78px;
  padding: 12px 16px;
  border-bottom: 1px solid #e4e6ef;
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(8px);
  box-shadow: 0 3px 10px rgba(31, 45, 61, 0.05);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
}

.editor-identity,
.editor-actions,
.history-actions {
  display: flex;
  align-items: center;
}

.editor-identity {
  min-width: 0;
}
.editor-actions {
  gap: 7px;
}
.history-actions {
  gap: 4px;
  padding-right: 7px;
  margin-right: 2px;
  border-right: 1px solid #e4e6ef;
}

.editor-symbol {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  margin-right: 11px;
  border-radius: 11px;
  background: #f0ebff;
  color: #8950fc;
  display: grid;
  place-items: center;
  font-size: 17px;
}

.editor-title-wrap {
  min-width: 0;
  width: 330px;
}

.editor-state {
  padding: 3px 7px;
  border-radius: 12px;
  font-size: 8px;
  font-weight: 700;
  text-transform: uppercase;

  i {
    width: 5px;
    height: 5px;
    margin-right: 4px;
    border-radius: 50%;
    background: currentColor;
    display: inline-block;
  }
  &.published {
    background: #e8fff3;
    color: #169f6c;
  }
  &.draft {
    background: #fff4de;
    color: #b76e00;
  }
}

.editor-origin {
  margin-left: 8px;
  color: #a1a5b7;
  font-size: 8px;
}

.editor-name {
  width: 100%;
  padding: 0;
  border: 0;
  border-bottom: 1px solid transparent;
  background: transparent;
  color: #181c32;
  font-size: 16px;
  font-weight: 800;
  text-overflow: ellipsis;

  &:focus {
    border-bottom-color: #8950fc;
    outline: none;
  }
}

.editor-layout {
  display: grid;
  grid-template-columns: 300px minmax(500px, 1fr) 330px;
  gap: 14px;
  padding: 0 16px;
  align-items: start;
}

.editor-sidebar,
.inspector-card,
.sheet-card {
  overflow: hidden;
}

.editor-sidebar,
.editor-right-column {
  position: sticky;
  top: calc(
    var(--etq-global-header-height) + var(--etq-editor-header-height) + 16px
  );
  max-height: calc(
    100vh - var(--etq-global-header-height) - var(--etq-editor-header-height) -
      34px
  );
}

.editor-sidebar {
  overflow-y: auto;
}

.editor-right-column {
  display: flex;
  flex-direction: column;
  gap: 14px;
  height: calc(
    100vh - var(--etq-global-header-height) - var(--etq-editor-header-height) -
      34px
  );
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  padding-right: 5px;
}

.sheet-card {
  flex: 0 0 auto;
}

.inspector-card {
  flex: 1 1 auto;
  min-height: 480px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.canvas-controls {
  min-height: 45px;
  margin-bottom: 10px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.canvas-control-group {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #7e8299;
  font-size: 9px;

  input {
    width: 100px;
    accent-color: #8950fc;
  }
  strong {
    width: 36px;
    color: #5e6278;
  }
}

.grid-toggle {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #7e8299;
  font-size: 9px;
  font-weight: 600;

  i {
    margin-right: 4px;
    color: #8950fc;
  }
}

.conversion-note {
  margin-top: 10px;
  padding: 11px 13px;
  border: 1px solid #ffe1a8;
  border-radius: 9px;
  background: #fff8e9;
  color: #8b621b;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 10px;
  line-height: 1.5;

  > div,
  span {
    display: block;
  }
}

.sample-panel {
  padding: 20px;
}
.etq-panel-kicker {
  color: #7e8299;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.sample-heading h5 {
  margin: 2px 0 5px;
  color: #263238;
  font-weight: 700;
}
.sample-heading p {
  color: #7e8299;
  font-size: 10px;
  line-height: 1.5;
}

.sample-company {
  margin-top: 15px;
  padding: 12px;
  border: 1px solid #e4e6ef;
  border-radius: 9px;
  background: #fff;
}

.sample-company-label {
  margin-bottom: 9px;

  span,
  small {
    display: block;
  }

  span {
    color: #3f4254;
    font-size: 10px;
    font-weight: 700;
  }

  small {
    margin-top: 2px;
    color: #a1a5b7;
    font-size: 8px;
  }
}

.selected-test-company {
  min-height: 44px;
  padding: 7px;
  border-radius: 7px;
  background: #f3f6f9;
  display: flex;
  align-items: center;
  gap: 8px;

  > span {
    width: 29px;
    height: 29px;
    flex: 0 0 29px;
    border-radius: 7px;
    background: #e5e0ff;
    color: #8950fc;
    display: grid;
    place-items: center;
  }

  > div {
    min-width: 0;
    flex: 1;
  }

  strong,
  small {
    display: block;
  }

  strong {
    overflow: hidden;
    color: #3f4254;
    font-size: 9px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    margin-top: 2px;
    color: #7e8299;
    font-size: 7px;
  }
}

.test-company-results {
  max-height: 150px;
  margin-top: 6px;
  border: 1px solid #e4e6ef;
  border-radius: 7px;
  overflow-y: auto;

  button {
    width: 100%;
    min-height: 34px;
    padding: 6px 8px;
    border: 0;
    border-bottom: 1px solid #eef0f5;
    background: #fff;
    color: #5e6278;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 8px;
    text-align: left;

    &:last-child {
      border-bottom: 0;
    }

    &:hover {
      background: #f7f5ff;
      color: #8950fc;
    }
  }
}

.sample-options {
  padding: 8px 12px;
  margin: 15px 0;
  border-radius: 9px;
  background: #f7f8fb;

  label {
    min-height: 31px;
    margin: 0;
    border-bottom: 1px solid #e9ebf2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #5e6278;
    font-size: 10px;

    &:last-child {
      border-bottom: 0;
    }
  }
}

.sample-fields label {
  color: #5e6278;
  font-size: 10px;
  font-weight: 600;
}

::v-deep .sidebar-tabs {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 7px;
  background: #fff;
  border-bottom: 1px solid #eef0f5;

  .nav-link {
    padding: 9px 4px;
    border-radius: 7px;
    color: #7e8299;
    font-size: 9px;
    font-weight: 700;

    i {
      display: block;
      margin-bottom: 4px;
    }
    &.active {
      background: #f0ebff;
      color: #8950fc;
    }
  }
}

@media (max-width: 1599.98px) {
  .editor-layout {
    grid-template-columns: 280px minmax(440px, 1fr) 300px;
  }
  .editor-title-wrap {
    width: 250px;
  }
  .editor-actions .btn {
    padding-left: 11px;
    padding-right: 11px;
  }
}

@media (max-width: 1299.98px) {
  .etiqueta-editor-page {
    --etq-editor-header-height: 128px;
  }
  .editor-header {
    align-items: flex-start;
    flex-direction: column;
  }
  .editor-actions {
    width: 100%;
    justify-content: flex-end;
  }
  .editor-layout {
    grid-template-columns: 280px minmax(0, 1fr);
  }
  .editor-right-column {
    position: static;
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: 1fr;
    height: auto;
    max-height: none;
    overflow: visible;
  }
  .inspector-card {
    overflow: visible;
  }
  .editor-sidebar {
    top: calc(
      var(--etq-global-header-height) + var(--etq-editor-header-height) + 16px
    );
    max-height: calc(
      100vh - var(--etq-global-header-height) - var(--etq-editor-header-height) -
        34px
    );
  }
}

@media (max-width: 899.98px) {
  .editor-layout {
    grid-template-columns: 1fr;
  }
  .editor-sidebar,
  .editor-right-column {
    position: static;
    max-height: none;
    overflow: visible;
  }
  .editor-right-column {
    grid-template-columns: 1fr;
  }
  .editor-actions {
    flex-wrap: wrap;
    justify-content: flex-start;
  }
  .history-actions {
    border-right: 0;
  }
}
</style>
